import { Box, Tooltip, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { useMemo } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { AsyncSelect } from 'src/components/forms';
import { ITableColumn, TableList } from 'src/components/table';
import { EntityWithIdAndDescriptionFields } from 'src/types';
import UseBgColor from 'src/@core/hooks/useBgColor';
import { OperacionAFOCLFields, OperacionAFOCLTablaValorFields } from '../types/fields';
import { AFOCLHeader, AFOCLRow } from '../hooks/useTablaValorAFOCL';
import { OperacionAFOCLTablaValorVista, OperacionAFOCLTablaValorVistaInicial } from '../types/dto';
import { useLoadOptions } from 'src/hooks/useLoadOptions';
import { useAFOPUAforoTipos } from '../../afopu/hooks/useAFOPUCache';

interface AFOCLTablaValoresEditorGridProps {
  formMethods: UseFormReturn<any>;
  headers: AFOCLHeader[];
  rows: AFOCLRow[];
  aforos: OperacionAFOCLTablaValorVista[] | OperacionAFOCLTablaValorVistaInicial[];
  isLoading: boolean;
  codPais: number;
}

const AFOCLTablaValoresEditorGrid = ({ formMethods, headers, rows, aforos, isLoading, codPais }: AFOCLTablaValoresEditorGridProps) => {
  const aforoTipos = useAFOPUAforoTipos(codPais);
  const isComponentLoading = isLoading || aforoTipos.isLoading;
  const loadAforoTipos = useLoadOptions({
    data: aforoTipos.aforoTipos, loading: aforoTipos.isLoading, isError: aforoTipos.isError, error: aforoTipos.error
  });
  const bgColors = UseBgColor();

  const { columns } = useMemo(() => {
    const fieldName = OperacionAFOCLFields.TablaValores;

    if (!headers || headers.length === 0) return { columns: [] };

    // mapa de elemento a item del formulario
    const indexMap = new Map<string, number>();
    aforos.forEach((item: any, idx: number) => {
      const key = `${item[OperacionAFOCLTablaValorFields.CodInstrumento]}-${item[OperacionAFOCLTablaValorFields.CodOperacionTipo]}`;
      indexMap.set(key, idx);
    });

    const cols: ITableColumn[] = [{
      label: 'INSTRUMENTO',
      value: OperacionAFOCLTablaValorFields.DescInstrumento,
      onRenderCell: (row: any) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold', py: 1 }}>
          {row.descInstrumento}
        </Typography>
      ),
    }];

    headers.forEach((header) => {
      cols.push({
        label: header.descOperacionTipo.toUpperCase(),
        onRenderCell: (row: AFOCLRow) => {
          const celda = row.celdas[header.codOperacionTipo];
          if (!celda) return <Typography variant="caption" sx={{ color: 'text.secondary', p: 1 }}>No Aplica</Typography>;

          const key = `${celda[OperacionAFOCLTablaValorFields.CodInstrumento]}-${celda[OperacionAFOCLTablaValorFields.CodOperacionTipo]}`;
          const itemIndex = indexMap.get(key);
          if (itemIndex === undefined) return <></>;

          return (
            <AFOCLTablaValorCelda
              control={formMethods.control}
              name={`${fieldName}.${itemIndex}.${OperacionAFOCLTablaValorFields.ValorCliente}`}
              valorPuente={celda[OperacionAFOCLTablaValorFields.ValorPuente]}
              permiteEditar={celda[OperacionAFOCLTablaValorFields.PermiteEditar]}
              loadOptions={loadAforoTipos}
              colorWarning={bgColors.primaryLight.color}
            />
          );
        }
      });
    });

    return { columns: cols };
  }, [headers, aforos, formMethods, loadAforoTipos, bgColors]);

  return (
    <TableList entityList={rows} columns={columns} isLoading={isComponentLoading} error={false} />
  );
};

export default AFOCLTablaValoresEditorGrid;


const AFOCLTablaValorCelda = ({
  control,
  name,
  valorPuente,
  permiteEditar,
  loadOptions,
  colorWarning
}: {
  control: any, name: string, valorPuente: string, permiteEditar: boolean, loadOptions: any, colorWarning: string
}) => {
  const valorActual = useWatch({ control, name });
  const isDifferent = valorActual !== valorPuente;

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', gap: 1, py: 1, px: 1, minWidth: '160px',
      borderRadius: '8px', border: '2px solid', borderColor: isDifferent ? 'warning.main' : 'transparent',
      transition: 'all 0.3s ease'
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
          Aforo Puente: <b>{valorPuente}</b>
        </Typography>
        {isDifferent && (
          <Tooltip title="El valor difiere del Aforo Puente">
            <Box sx={{ display: 'flex' }}><Icon icon="mdi:alert-circle-outline" color={colorWarning} width="14" /></Box>
          </Tooltip>
        )}
      </Box>
      <AsyncSelect
        loadOptions={loadOptions}
        control={control}
        name={name}
        disabled={!permiteEditar}
        valueField={EntityWithIdAndDescriptionFields.Description}
        selectNotNulleable
      />
    </Box>
  );
};
